import { axiosClient } from '@/shared';
import { ApiResponse } from '@/shared/types';
import { AssetUploadPresignedRequest } from './post-presigned-url';

// BE 응답 타입
export interface PartPresignedUrl {
  partNumber: number;
  presignedUrl: string;
}

export interface MultipartUploadInitResponse {
  /** 서버에서 생성된 Content ID */
  id: number;
  /** S3 Multipart Upload ID */
  uploadId: string;
  /** 파트 크기 (bytes) */
  partSize: number;
  /** 각 파트별 presigned URL 목록 */
  partPresignedUrls: PartPresignedUrl[];
  /** 썸네일 업로드용 presigned URL (비디오인 경우) */
  thumbnailPreSignedUrl: string | null;
}

export interface CompletedPart {
  partNumber: number;
  eTag: string;
}

/**
 * Multipart Upload 시작 (Initiate)
 *
 * 기존 presigned URL 요청과 동일한 body를 사용합니다.
 * BE에서 Content 생성 + S3 CreateMultipartUpload + 파트별 presigned URL 발급을 수행합니다.
 *
 * POST /v2.0/asset/upload/multipart/initiate
 */
export const initiateMultipartUploadApi = async (
  data: AssetUploadPresignedRequest,
): Promise<ApiResponse<MultipartUploadInitResponse>> => {
  const response = await axiosClient.post<
    ApiResponse<MultipartUploadInitResponse>
  >('/v2.0/asset/upload/multipart/initiate', data);
  return response.data;
};

/**
 * 기존 Content에 대한 Multipart Upload 시작
 *
 * 프로젝트 생성 플로우에서 이미 Content가 생성된 경우 사용합니다.
 * contentId만으로 해당 Content의 S3 정보를 활용하여 멀티파트 업로드를 시작합니다.
 *
 * POST /v2.0/asset/upload/multipart/initiate/{contentId}
 */
export const initiateMultipartUploadForContentApi = async (
  contentId: number,
): Promise<ApiResponse<MultipartUploadInitResponse>> => {
  const response = await axiosClient.post<
    ApiResponse<MultipartUploadInitResponse>
  >(`/v2.0/asset/upload/multipart/initiate/${contentId}`);
  return response.data;
};

/**
 * Multipart Upload 완료 (Complete)
 *
 * 모든 파트 업로드 완료 후 S3 CompleteMultipartUpload를 수행합니다.
 *
 * POST /v2.0/asset/upload/multipart/complete
 */
export const completeMultipartUploadApi = async (params: {
  contentId: number;
  uploadId: string;
  parts: CompletedPart[];
  folderId?: number;
}): Promise<void> => {
  await axiosClient.post('/v2.0/asset/upload/multipart/complete', params);
};

/**
 * Multipart Upload 중단 (Abort)
 *
 * 업로드 실패 시 S3 AbortMultipartUpload를 수행합니다.
 * abort 실패해도 BE 배치에서 정리되므로 에러를 무시합니다.
 *
 * POST /v2.0/asset/upload/multipart/abort
 */
export const abortMultipartUploadApi = async (params: {
  contentId: number;
  uploadId: string;
}): Promise<void> => {
  try {
    await axiosClient.post('/v2.0/asset/upload/multipart/abort', params);
  } catch (e) {
    // abort 실패해도 BE 배치에서 정리됨
    console.error('[Multipart] abort 실패:', e);
  }
};
